import { Keypair, PublicKey } from '@solana/web3.js'

import useApproveDelegate from '@src/chains/solana/approveDelegate'
import { SOLANA_CONNECTION } from '@src/chains/solana/config'
import useDepositForBurn from '@src/chains/solana/depositForBurn'
import usePlaceOrder from '@src/chains/solana/placeOrder'
import { useWrapSOL } from '@src/chains/solana/useWrapSOL'
import { ChainId } from '@src/configs/chainInfo'
import { getSolanaGasFeeMetrics } from '@src/hooks/solana/useGasFeeMetrics'
import { GasFeeMetrics, GasFeeSimulationResults } from '@src/types/gasFees'
import { PrecomputeGasResults, TransactionType } from '@src/types/gasPrices'
import { IOrderDirectionParameter, IOrderFundingParameter } from '@src/types/order'
import { Address } from '@src/types/token'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('usePrecomputedSolanaGasPrices')

export const usePrecomputedSolanaGasPrices = (swapContracts: Record<string, any> | undefined) => {
    const computeSolanaCCTPFees = async (
        feeMetrics: GasFeeMetrics,
        getDepositForBurnData: Function
    ): Promise<GasFeeSimulationResults> => {
        const amount = BigInt('0xffffffffffffff')
        const cctp_id = 5
        const destinationAddressInBytes32 = '0xf'.padEnd(42, 'f')
        const dummyPk = Keypair.generate().publicKey
        const dummyWalletAddress = dummyPk.toBase58()

        try {
            const { versionedTransaction } = await getDepositForBurnData(
                amount,
                destinationAddressInBytes32,
                cctp_id,
                dummyWalletAddress,
                true,
                dummyPk
            )

            if (!versionedTransaction) {
                throw new Error('versionedTransaction not found')
            }

            const feeResponse = await SOLANA_CONNECTION.getFeeForMessage(versionedTransaction.message, 'confirmed')

            if (typeof feeResponse.value === 'number') {
                return {
                    totalGasUnits: BigInt(0),
                    indivdualGasUnits: [BigInt(0)],
                    totalFeePerGasUnit: BigInt(0),
                    feeMetrics: feeMetrics,
                    solanaResults: {
                        feeInLamports: BigInt(feeResponse.value),
                    },
                }
            }
            throw new Error('Invalid fee response')
        } catch (e) {
            logger.error('Error computing Solana CCTP fees:', e)
            return {} as GasFeeSimulationResults
        }
    }

    const computeSolanaWrapFees = async (
        feeMetrics: GasFeeMetrics,
        getWrapData: Function
    ): Promise<GasFeeSimulationResults> => {
        logger.info(`[computeSolanaWrapFees]: function called.`)

        // Use the same dummy amount for simulation purposes.
        const amount = BigInt('100')
        const dummyPk = Keypair.generate().publicKey

        try {
            // Construct the versioned wrap transaction using the provided getWrapData function.
            const { versionedTransaction } = await getWrapData(amount, dummyPk)

            logger.info(`[computeSolanaWrapFees]: versionedTransaction=`, versionedTransaction)

            if (!versionedTransaction) {
                throw new Error('versionedTransaction not found')
            }

            const feeResponse = await SOLANA_CONNECTION.getFeeForMessage(versionedTransaction.message, 'confirmed')

            if (typeof feeResponse.value === 'number') {
                return {
                    totalGasUnits: BigInt(0),
                    indivdualGasUnits: [BigInt(0)],
                    totalFeePerGasUnit: BigInt(0),
                    feeMetrics,
                    solanaResults: {
                        // Add multiplier to overestimate
                        // Multiplier is empirically determined based on phantom wallet gas estimation
                        feeInLamports: BigInt(feeResponse.value) * BigInt(40),
                    },
                }
            }
            throw new Error('Invalid fee response')
        } catch (e) {
            logger.error('Error computing Solana wrap fees:', e)
            return {} as GasFeeSimulationResults
        }
    }

    const computeSolanaPlaceOrderFees = async (
        feeMetrics: GasFeeMetrics,
        getPlaceOrderData: Function
    ): Promise<GasFeeSimulationResults> => {
        logger.info(`[computeSolanaPlaceOrderFees]: function called.`)

        // Use the same dummy data for simulation purposes.
        const dummyOrderDir: IOrderDirectionParameter = {
            srcAsset: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
            dstAsset: '0xf97f4df75117a78c1A5a0DBb814Af92458539FB4',
            dstLzc: 30110,
        }
        const dummyOrderFunding: IOrderFundingParameter = {
            srcQuantity: BigInt('1000000'),
            dstQuantity: BigInt('53771317470935976'),
            bondAsset: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
            bondAmount: BigInt('999800'),
            bondFee: 1,
        }
        const targetAddress: Address = '0x581190A5251c211dAe57304449c5625DBeDFa2af' as Address
        const dummyPk = Keypair.generate().publicKey

        logger.info(`[computeSolanaPlaceOrderFees]: function called.`)

        try {
            // Construct the versioned wrap transaction using the provided getWrapData function.
            const { versionedTransaction } = await getPlaceOrderData(
                dummyOrderDir,
                dummyOrderFunding,
                targetAddress,
                dummyPk
            )

            logger.info(`[computeSolanaPlaceOrderFees]: versionedTransaction=`, versionedTransaction)

            if (!versionedTransaction) {
                throw new Error('versionedTransaction not found')
            }

            const feeResponse = await SOLANA_CONNECTION.getFeeForMessage(versionedTransaction.message, 'confirmed')

            if (typeof feeResponse.value === 'number') {
                return {
                    totalGasUnits: BigInt(0),
                    indivdualGasUnits: [BigInt(0)],
                    totalFeePerGasUnit: BigInt(0),
                    feeMetrics,
                    solanaResults: {
                        feeInLamports: BigInt(feeResponse.value),
                    },
                }
            }
            throw new Error('Invalid fee response')
        } catch (e) {
            logger.error('Error computing Solana placeOrder fees:', e)
            return {} as GasFeeSimulationResults
        }
    }

    const computeSolanaApproveDelegateFees = async (
        feeMetrics: GasFeeMetrics,
        getApproveDelegateData: Function
    ): Promise<GasFeeSimulationResults> => {
        logger.info(`[computeSolanaApproveDelegateFees]: function called.`)

        // USDC on mainnet
        const dummyTokenMint = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v')

        // Warning: We must use a public key for which the USDC ATA exists.
        const dummyPk = new PublicKey('ETcQe8yqn8sR2jxiaGUe3eEKUFZUi1DqdwPedCE8x7WE')

        logger.info(`[computeSolanaApproveDelegateFees]: function called.`)

        try {
            // Construct the versioned wrap transaction using the provided getWrapData function.
            const { versionedTransaction } = await getApproveDelegateData(dummyTokenMint, dummyPk)

            logger.info(`[computeSolanaApproveDelegateFees]: versionedTransaction=`, versionedTransaction)

            if (!versionedTransaction) {
                throw new Error('versionedTransaction not found')
            }

            const feeResponse = await SOLANA_CONNECTION.getFeeForMessage(versionedTransaction.message, 'confirmed')

            if (typeof feeResponse.value === 'number') {
                return {
                    totalGasUnits: BigInt(0),
                    indivdualGasUnits: [BigInt(0)],
                    totalFeePerGasUnit: BigInt(0),
                    feeMetrics,
                    solanaResults: {
                        feeInLamports: BigInt(feeResponse.value),
                    },
                }
            }
            throw new Error('Invalid fee response')
        } catch (e) {
            logger.error('Error computing Solana approveDelegate fees:', e)
            return {} as GasFeeSimulationResults
        }
    }

    const { getDepositForBurnData } = useDepositForBurn()
    const { getWrapData } = useWrapSOL()
    const { getPlaceOrderData } = usePlaceOrder()
    const { getApproveDelegateData } = useApproveDelegate()

    const computeSolanaGasPrices = async (): Promise<PrecomputeGasResults> => {
        const feeMetrics = await getSolanaGasFeeMetrics()

        return {
            [ChainId.Solana]: {
                feeMetrics,
                results: {
                    [TransactionType.WRAP]: await computeSolanaWrapFees(feeMetrics, getWrapData),
                    [TransactionType.APPROVE]: await computeSolanaApproveDelegateFees(
                        feeMetrics,
                        getApproveDelegateData
                    ),
                    [TransactionType.PLACE_ORDER]: await computeSolanaPlaceOrderFees(feeMetrics, getPlaceOrderData),
                    [TransactionType.TRANSFER]: {} as GasFeeSimulationResults,
                    [TransactionType.DEPOSIT_FOR_BURN]: await computeSolanaCCTPFees(feeMetrics, getDepositForBurnData),
                },
            },
        }
    }

    return { computeSolanaGasPrices }
}
